import type { Dispatch, SetStateAction } from "react";
import {
  normalizeCustomerOfferHighlightFromApi,
  parseCustomerOfferHighlightsFromApi,
} from "@/shared/constants/customerOfferPresets";

export const PROFILE_BUILDER_DRAFT_VERSION = 1 as const;

export type ProfileBuilderDraftV1 = {
  v: typeof PROFILE_BUILDER_DRAFT_VERSION;
  updatedAtMs: number;
  formData: {
    userName: string;
    userDesignation: string;
    userPhoneNumber: string;
    userPhoneCountryCode: string;
    companyLegalName: string;
    state: string;
    tradeName: string;
    country: string;
    registrationNumber: string;
    registrationIdType: string | null;
    yearOfIncorporation: string | null;
    companyWebsite: string;
    companyEmail: string;
    phoneCountryCode: string;
    phoneNumber: string;
    companyDescription: string;
    subDescription: string;
    operations: string[];
    industryCategory: string[];
    subIndustry: string[];
    whoAmI: string[];
    collaborationIntent: string[];
    coreCapabilities: string[];
    secondaryCapabilities: string[];
    keyDifferentiators: string;
    customerOfferPresets: string[];
    customerOfferOtherText: string;
    technologies: string[];
    additionalTechInfrastructure: string[];
    headquartersCountry: string;
    headquartersCity: string;
    currentAddress: string;
    operatingCountries: string[];
    preferredRegions: string[];
    companyRegisteredAddress: string;
    nationalIndustryClassificationCode: string;
    additionalCompanyStatus: string;
    directorName: string;
    directorDesignation: string;
    directorEmail: string;
    paidupCapitalBand: string;
    turnoverBand: string;
    verificationBusinessType: string;
  };
  products: any[];
  services: any[];
  additionalDocs: any[];
  aiImages: string[];
  activeTab: string | null;
};

export function buildProfileBuilderDraftPayload(input: {
  formData: ProfileBuilderDraftV1["formData"] & { yearOfIncorporation: Date | null | string };
  products: any[];
  services: any[];
  additionalDocs: any[];
  aiImages: string[];
  activeTab: string | null;
}): ProfileBuilderDraftV1 {
  const fd = input.formData;
  const yoi = fd.yearOfIncorporation as Date | string | null | undefined;
  const yearOfIncorporationSerialized =
    yoi instanceof Date
      ? yoi.toISOString()
      : typeof yoi === "string" && yoi.length > 0
        ? yoi
        : null;
  const cleanAdditionalDocs = (input.additionalDocs || []).map((d: any) => ({
    ...d,
    file: null,
  }));

  return {
    v: PROFILE_BUILDER_DRAFT_VERSION,
    updatedAtMs: Date.now(),
    formData: {
      ...fd,
      yearOfIncorporation: yearOfIncorporationSerialized,
    } as ProfileBuilderDraftV1["formData"],
    products: input.products || [],
    services: input.services || [],
    additionalDocs: cleanAdditionalDocs,
    aiImages: input.aiImages || [],
    activeTab: input.activeTab ?? "company-profile",
  };
}

function parseDraft(d: unknown): ProfileBuilderDraftV1 | null {
  if (!d || typeof d !== "object") return null;
  const o = d as ProfileBuilderDraftV1;
  if (o.v !== PROFILE_BUILDER_DRAFT_VERSION) return null;
  if (!o.formData || typeof o.formData !== "object") return null;
  return o;
}

/** True if user should see “resume draft?” — not shown for first visit / empty saves. */
export function isProfileBuilderDraftMeaningful(d: unknown): boolean {
  const parsed = parseDraft(d);
  if (!parsed) return false;

  const skip = new Set([
    "userName",
    "userDesignation",
    "userPhoneNumber",
    "userPhoneCountryCode",
    "companyEmail",
  ]);

  for (const key of Object.keys(parsed.formData)) {
    if (skip.has(key)) continue;
    const v = (parsed.formData as Record<string, unknown>)[key];
    if (v == null || v === "") continue;
    if (Array.isArray(v) && v.length === 0) continue;
    return true;
  }

  if (
    (parsed.products || []).some((p: any) => `${p?.name ?? ""}`.trim() || `${p?.description ?? ""}`.trim())
  )
    return true;
  if (
    (parsed.services || []).some((s: any) => `${s?.name ?? ""}`.trim() || `${s?.description ?? ""}`.trim())
  )
    return true;
  if (
    (parsed.additionalDocs || []).some(
      (doc: any) => `${doc?.name ?? ""}`.trim() || `${doc?.url ?? ""}`.trim()
    )
  )
    return true;
  if ((parsed.aiImages || []).length > 0) return true;
  if (parsed.activeTab && parsed.activeTab !== "company-profile") return true;
  return false;
}

export function applyProfileBuilderDraft(
  draft: ProfileBuilderDraftV1,
  setters: {
    setFormData: Dispatch<SetStateAction<any>>;
    setProducts: Dispatch<SetStateAction<any[]>>;
    setServices: Dispatch<SetStateAction<any[]>>;
    setAdditionalDocs: Dispatch<SetStateAction<any[]>>;
    setAiImages: Dispatch<SetStateAction<string[]>>;
    setActiveTab: Dispatch<SetStateAction<string | null>>;
  }
) {
  const fd = draft.formData as any;
  const sub = fd.subIndustry;
  const subIndustryNorm = Array.isArray(sub) ? sub : sub ? [String(sub)] : [];

  setters.setFormData((prev: any) => {
    const next: any = {
      ...prev,
      ...fd,
      subIndustry: subIndustryNorm,
      yearOfIncorporation: fd.yearOfIncorporation ? new Date(fd.yearOfIncorporation as string) : null,
    };

    const legacyHighlight = normalizeCustomerOfferHighlightFromApi(fd.customerOfferHighlight);
    const presetsFromDraft = Array.isArray(fd.customerOfferPresets)
      ? fd.customerOfferPresets
      : typeof fd.customerOfferPreset === "string" && fd.customerOfferPreset.trim()
        ? [fd.customerOfferPreset.trim()]
        : [];

    if (legacyHighlight.length > 0 && presetsFromDraft.length === 0) {
      const parsed = parseCustomerOfferHighlightsFromApi(legacyHighlight);
      next.customerOfferPresets = parsed.presets;
      next.customerOfferOtherText = parsed.otherText;
    } else {
      next.customerOfferPresets = presetsFromDraft;
    }

    next.customerOfferPresets = next.customerOfferPresets ?? [];
    next.customerOfferOtherText = next.customerOfferOtherText ?? "";
    delete next.customerOfferHighlight;
    delete next.customerOfferPreset;

    return next;
  });
  setters.setProducts(Array.isArray(draft.products) ? draft.products : []);
  setters.setServices(Array.isArray(draft.services) ? draft.services : []);
  setters.setAdditionalDocs(Array.isArray(draft.additionalDocs) ? draft.additionalDocs : []);
  setters.setAiImages(Array.isArray(draft.aiImages) ? draft.aiImages : []);
  setters.setActiveTab(
    !draft.activeTab || draft.activeTab === "images" ? "company-profile" : draft.activeTab
  );
}
